// employee add edit
import React, { forwardRef, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Input, Textarea } from "../../../components/ui/input";
import { DatePicker, Select, Upload, Checkbox } from "antd";
import { ChevronLeftIcon, Copy, Eye, EyeClosed, Plus, Save, UploadCloudIcon, UserIcon, X } from "lucide-react";
import { ContainerCard } from "../../../components/ui/cards";
import { cn } from "../../../utils/tailwindUtils";
import { Button } from "../../../components/ui/button";
import useApiMethods from "../../../lib/useApiMethods";
import { ASSET_REGISTRY, EMPLOYEE_DEPARTMENT, EMPLOYEE_DESIGNATION, EMPLOYEE_PERSONAL_INFO, EMPLOYEE_GROUP, EMPLOYEE_TYPE, EMPLOYEE_SHIFT, USERGROUP, EMPLOYEE_INTERN_INFO, EMPLOYEE_CANDIDATE_INFO } from "../../../constants/api";
import ToastMsg from "../../../components/common/ToastMsg";
import dayjs from "dayjs";

const { Option } = Select;

export const relationshipEnum = [
    { value: 'mother', text: 'Mother' },
    { value: 'father', text: 'Father' },
    { value: 'spouse', text: 'Spouse' },
    { value: 'brother', text: 'Brother' },
    { value: 'sister', text: 'Sister' },
    { value: 'friend', text: 'Friend' },
    { value: 'others', text: 'Others' },
];

export const bloodGroupEnum = [
    { value: 'O-Ve', text: 'O-Ve' },
    { value: 'O+Ve', text: 'O+Ve' },
    { value: 'A+Ve', text: 'A+Ve' },
    { value: 'A-Ve', text: 'A-Ve' },
    { value: 'A1+Ve', text: 'A1+Ve' },
    { value: 'A1-Ve', text: 'A1-Ve' },
    { value: 'A1B+Ve', text: 'A1B+Ve' },
    { value: 'A1B-Ve', text: 'A1B-Ve' },
    { value: 'A2+Ve', text: 'A2+Ve' },
    { value: 'A2-Ve', text: 'A2-Ve' },
    { value: 'A2B+Ve', text: 'A2B+Ve' },
    { value: 'A2B-Ve', text: 'A2B-Ve' },
    { value: 'AB+Ve', text: 'AB+Ve' },
    { value: 'AB-Ve', text: 'AB-Ve' },
    { value: 'B+Ve', text: 'B+Ve' },
    { value: 'B-Ve', text: 'B-Ve' },
];

export const genderEnum = [
    { value: 'male', text: 'Male' },
    { value: 'female', text: 'Female' },
    { value: 'others', text: 'Others' },
];

export const titleEnum = [
    { value: 'Mr', text: 'Mr' },
    { value: 'Ms', text: 'Ms' },
    { value: 'Mrs', text: 'Mrs' },
    { value: 'Dr', text: 'Dr' },
];

const EmployeeForm = forwardRef(({
    pagetitle = "Create Employee",
    pageName,
    url = '',
}, ref) => {

    const navigate = useNavigate();
    const { fetch, create, update } = useApiMethods()
    const location = useLocation()
    let directEmployeeOnboardFlow = location.pathname === '/employee/add'

    url = location.pathname === '/employee/add' ?
        EMPLOYEE_PERSONAL_INFO : location.pathname === '/candidate/add' ?
            EMPLOYEE_CANDIDATE_INFO : location.pathname === '/intern/add' ?
                EMPLOYEE_INTERN_INFO : '' // updating the url based on the route

    const { employeeId: employeeID = null, isEdit } = location.state || {};
    const isEditMode = Boolean(isEdit);
    const [profile, setProfile] = useState({})

    const default_asset_info = [{
        asset_registry: undefined,
        asset_name: "",
        handing_over_by: undefined,
        issued_date: null,
        remarks: ""
    }]

    const [formData, setFormData] = useState({
        professional_info: {
            is_active: true,
            profile_document: [],
            employee_id: null,
            display_name: null,
            department: undefined,
            designation: undefined,
            reporting_manager: undefined,
            shift: undefined,
            employee_group: undefined,
            employee_type: undefined,
            work_location: null,
            date_of_joining: null,
            date_of_relieving: null,
        },
        personal_info: {
            title: undefined,
            first_name: null,
            last_name: null,
            gender: undefined,
            dob: null,
            mobile_number: null,
            email_address: null,
            permanent_address: null,
            pin_code: null,
            city: null,
            state: null,
            country: null,
            contact_person: null,
            relationship: undefined,
            contact_person_mobile_number: null,
            is_admin: false,
            blood_group: undefined,
            present_address: null,
            present_pin_code: null,
            present_city: null,
            present_state: null,
            present_country: null,
            emergency_contact_person: null,
            emergency_contact_person_relationship: null,
            emergency_contact_person_mobile_number: null,
        },
        bank_info: {
            bank_name: null,
            account_number: null,
            ifsc_code: null,
            account_holder_name: null,
            branch: null,
        },
        asset_info: default_asset_info.map(obj => ({ ...obj })),
        user_creation_info: {
            user_name: null,
            password: null,
            user_group: undefined,
            user_id: undefined,
            email_address: null
        }
    });
    const [loading, setLoading] = useState(false)
    const [screenLoader, setScreenLoader] = useState(false)
    const [copyAddress, setCopyAddress] = useState(false);

    const [dropdownData, setDropdownData] = useState({
        department: [],
        designation: [],
        empinfo: [],
        empgroup: [],
        emptype: [],
        shift: [],
        usergroup: [],
        assetreg: [],
    })

    // Add a state to track if dropdowns are loaded
    const [dropdownsLoaded, setDropdownsLoaded] = useState(false);

    // common api calls to fetch the datas
    const getDepartmentList = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_DEPARTMENT, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getDesignationList = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_DESIGNATION, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getEmployeeList = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_PERSONAL_INFO, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getEmployeeGroup = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_GROUP, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getEmployeeType = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_TYPE, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getEmployeeShift = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_SHIFT, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getUserGroup = useCallback(async () => {
        try {
            const apiData = await fetch(USERGROUP, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getAssetRegistry = useCallback(async () => {
        try {
            const apiData = await fetch(ASSET_REGISTRY, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const fetchDropdown = async () => {
        setScreenLoader(true)
        setLoading(true)
        const [department, designation, empinfo, empgroup, emptype, shift, usergroup, assetreg] = await Promise.all([
            getDepartmentList(),
            getDesignationList(),
            getEmployeeList(),
            getEmployeeGroup(),
            getEmployeeType(),
            getEmployeeShift(),
            getUserGroup(),
            getAssetRegistry()
        ])

        const dropdowns = {
            department: department || [],
            designation: designation || [],
            empinfo: empinfo || [],
            empgroup: empgroup || [],
            emptype: emptype || [],
            shift: shift || [],
            usergroup: usergroup || [],
            assetreg: assetreg || [],
        };

        setDropdownData(dropdowns);
        setScreenLoader(false)
        setLoading(false)
        setDropdownsLoaded(true);

        return dropdowns; // Return dropdowns for potential use
    }

    const mapApiDataToFormData = (apiData) => {
        if (!apiData) return {};

        setLoading(true)

        // Store profile URL in state for ProfileUpload component
        const profileUrl = apiData.professional_info?.profile;
        setProfile(profileUrl);

        return {
            professional_info: {
                is_active: apiData.professional_info?.is_active ?? true,
                profile_document: [],
                profile: apiData.professional_info?.profile,
                employee_id: apiData.employee_id || null,
                display_name: apiData.professional_info?.display_name || null,
                department: apiData.department_id || apiData.professional_info?.department || undefined,
                designation: apiData.designation_id || apiData.professional_info?.designation || undefined,
                reporting_manager: apiData.reporting_manager_id || apiData.professional_info?.reporting_manager || undefined,
                shift: apiData.professional_info?.shift || undefined,
                employee_group: apiData.employee_group_id || apiData.professional_info?.employee_group || undefined,
                employee_type: apiData.professional_info?.employee_type || undefined,
                work_location: apiData.professional_info?.work_location || null,
                date_of_joining: apiData.date_of_joining || apiData.professional_info?.date_of_joining ? dayjs(apiData.date_of_joining || apiData.professional_info.date_of_joining) : null,
                date_of_relieving: apiData.professional_info?.date_of_releving ? dayjs(apiData.professional_info.date_of_releving) : null,
            },
            personal_info: {
                title: apiData.title || apiData.personal_info?.title || undefined,
                first_name: apiData.first_name || apiData.personal_info?.first_name || null,
                last_name: apiData.last_name || apiData.personal_info?.last_name || null,
                gender: apiData.personal_info?.gender || undefined,
                dob: apiData.personal_info?.date_of_birth ? dayjs(apiData.personal_info.date_of_birth) : null,
                mobile_number: apiData.personal_info?.mobile_number || null,
                email_address: apiData.personal_info?.email_address || null,
                permanent_address: apiData.personal_info?.permanent_address || null,
                pin_code: apiData.personal_info?.pin_code || null,
                city: apiData.personal_info?.city || null,
                state: apiData.personal_info?.state || null,
                country: apiData.personal_info?.country || null,
                contact_person: apiData.personal_info?.contact_person || null,
                relationship: apiData.personal_info?.relationship || undefined,
                contact_person_mobile_number: apiData.personal_info?.contact_person_mobile_number || null,
                is_admin: apiData.personal_info?.is_admin || false,
                blood_group: apiData.personal_info?.blood_group || undefined,
                present_address: apiData.personal_info?.present_address || null,
                present_pin_code: apiData.personal_info?.present_pin_code || null,
                present_city: apiData.personal_info?.present_city || null,
                present_state: apiData.personal_info?.present_state || null,
                present_country: apiData.personal_info?.present_country || null,
                emergency_contact_person: apiData.personal_info?.emergency_contact_person || null,
                emergency_contact_person_relationship: apiData.personal_info?.emergency_contact_person_relationship || null,
                emergency_contact_person_mobile_number: apiData.personal_info?.emergency_contact_person_mobile_number || null,
            },
            bank_info: {
                bank_name: apiData.bank_info?.bank_name || null,
                account_number: apiData.bank_info?.account_number || null,
                ifsc_code: apiData.bank_info?.ifsc_code || null,
                account_holder_name: apiData.bank_info?.account_holder_name || null,
                branch: apiData.bank_info?.branch || null,
            },
            asset_info: apiData.asset_info && apiData.asset_info.length > 0
                ? apiData.asset_info.map(asset => ({
                    asset_registry: asset.asset_registry || undefined,
                    asset_name: asset.asset_name || "",
                    handing_over_by: asset.handing_over_by || undefined,
                    issued_date: asset.issued_date ? dayjs(asset.issued_date) : null,
                    remarks: asset.remarks || ""
                }))
                : default_asset_info.map(obj => ({ ...obj })),
            user_creation_info: {
                user_name: apiData.user_creation_info?.user_name || null,
                password: apiData.user_creation_info?.password || null,
                user_group: apiData.user_creation_info?.user_group_id || undefined,
                user_id: apiData.user_creation_info?.user_id || undefined,
                email_address: apiData.user_creation_info?.profesional_mail || null
            }
        };
    };

    const getEmployeeDetailsById = async (id) => {
        if (!dropdownsLoaded) {
            ToastMsg('error', 'Dropdowns not loaded yet, skipping employee data fetch');
            return;
        }

        setLoading(true)
        try {
            const apiData = await fetch(EMPLOYEE_PERSONAL_INFO, { id: id });
            if (apiData?.status === "success" && apiData.data) {
                const empData = apiData.data.result[0];

                // Map the API data to form data and update form state
                const mappedFormData = mapApiDataToFormData(empData);

                setFormData(mappedFormData);

                // Set copy address state based on API data
                if (empData.personal_info?.is_address_copy) {
                    setCopyAddress(true);
                }
            }
        } catch (error) {
            console.error('Error fetching employee details:', error);
            ToastMsg('error', 'Failed to fetch employee details');
        } finally {
            setLoading(false)
        }
    }

    // Initialize all data in sequence
    useEffect(() => {
        const initializeAllData = async () => {
            await fetchDropdown(); // Wait for dropdowns to load first
        };

        initializeAllData();
    }, []);

    // Fetch employee details when dropdowns are loaded and employeeID is available
    useEffect(() => {
        if (dropdownsLoaded && employeeID) {
            // console.log('Dropdowns loaded, fetching employee details...');
            getEmployeeDetailsById(employeeID);
        }
    }, [dropdownsLoaded, employeeID]);

    // Handle nested form data updates
    const handleInputChange = (section, field, value) => {
        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    // Handle address copying
    const handleCopyAddressToggle = (checked) => {
        setCopyAddress(checked);
        if (checked) {
            setFormData(prev => ({
                ...prev,
                personal_info: {
                    ...prev.personal_info,
                    present_address: prev.personal_info.permanent_address,
                    present_pin_code: prev.personal_info.pin_code,
                    present_city: prev.personal_info.city,
                    present_state: prev.personal_info.state,
                    present_country: prev.personal_info.country
                }
            }));
        }
    };

    // Asset information handlers
    const handleAssetChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            asset_info: prev.asset_info.map((asset, i) =>
                i === index ? { ...asset, [field]: value } : asset
            )
        }));
    };

    const handleAddAsset = () => {
        setFormData(prev => ({
            ...prev,
            asset_info: [
                ...prev.asset_info,
                {
                    asset_registry: undefined,
                    asset_name: "",
                    handing_over_by: undefined,
                    issued_date: null,
                    remarks: ""
                }
            ]
        }));
    };

    const handleRemoveAsset = (index) => {
        if (formData.asset_info.length > 1) {
            setFormData(prev => ({
                ...prev,
                asset_info: prev.asset_info.filter((_, i) => i !== index)
            }));
        }
    };

    // Handle asset name auto-fill when asset registry is selected
    const handleAssetRegistryChange = (index, value) => {
        const selectedAsset = dropdownData.assetreg.find(asset => asset.id === value);
        handleAssetChange(index, 'asset_registry', value);
        if (selectedAsset) {
            handleAssetChange(index, 'asset_name', selectedAsset.asset_name);
        }
    };

    const handleRoleToggle = () => {
        setFormData(prev => ({
            ...prev,
            personal_info: {
                ...prev.personal_info,
                is_admin: !prev.personal_info.is_admin
            }
        }));
    };

    const handleFileUpload = (fileList, action) => {
        // console.log('File upload received:', fileList);
        setFormData(prev => ({
            ...prev,
            professional_info: {
                ...prev.professional_info,
                profile_document: fileList || [], // Store the file list directly
                profile: action === 'removed' && null
            }
        }));

        // // Also update the profile state for immediate preview
        // if (fileList && fileList.length > 0 && fileList[0]?.originFileObj) {
        //     const imageUrl = URL.createObjectURL(fileList[0].originFileObj);
        //     setProfile(imageUrl);
        // } else {
        //     setProfile(null);
        // }
    };

    const handleBack = () => navigate(-1);

    // Helper function to get file extension
    const getFileExtension = (filename) => {
        return filename.slice((filename.lastIndexOf(".") - 1 >>> 0) + 2);
    };

    // Remove file extension (for profile name)
    const removeFileExtension = (filename) => {
        return filename.replace(/\.[^/.]+$/, "");
    };

    // Date validation
    const isValidDate = (date) => {
        return date && !isNaN(new Date(date).getTime());
    };

    const handleSave = async () => {
        try {
            setLoading(true);

            // Validation function
            const validateForm = () => {
                const errors = [];

                // Professional Information validations
                if (!formData.professional_info.display_name?.trim()) {
                    errors.push("Display Name is required");
                }
                if (!formData.professional_info.department) {
                    errors.push("Department is required");
                }
                if (!formData.professional_info.designation) {
                    errors.push("Designation is required");
                }
                if (!formData.professional_info.shift) {
                    errors.push("Shift is required");
                }
                if (!formData.professional_info.employee_type) {
                    errors.push("Employee Type is required");
                }
                if (!formData.professional_info.date_of_joining) {
                    errors.push("Date of Joining is required");
                }

                // Personal Information validations
                if (!formData.personal_info.title) {
                    errors.push("Title is required");
                }
                if (!formData.personal_info.first_name?.trim()) {
                    errors.push("First Name is required");
                }
                if (!formData.personal_info.last_name?.trim()) {
                    errors.push("Last Name is required");
                }
                if (!formData.personal_info.gender) {
                    errors.push("Gender is required");
                }
                if (!formData.personal_info.dob) {
                    errors.push("Date of Birth is required");
                }
                if (!formData.personal_info.mobile_number?.trim()) {
                    errors.push("Personal Mobile No is required");
                }
                if (!formData.personal_info.email_address?.trim()) {
                    errors.push("Personal Email ID is required");
                }

                // Address validations
                if (!formData.personal_info.city?.trim()) {
                    errors.push("Permanent Address City is required");
                }
                if (!formData.personal_info.state?.trim()) {
                    errors.push("Permanent Address State is required");
                }

                // User Credentials validations (only for direct employee onboard flow)
                if (directEmployeeOnboardFlow) {
                    if (!formData.user_creation_info.user_name?.trim()) {
                        errors.push("Username is required");
                    }
                    if (!formData.user_creation_info.password?.trim()) {
                        errors.push("Password is required");
                    }
                    if (!formData.user_creation_info.user_group) {
                        errors.push("User Group is required");
                    }
                    if (!formData.user_creation_info.email_address?.trim()) {
                        errors.push("Professional Email is required");
                    }
                }

                // Email format validation
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (formData.personal_info.email_address && !emailRegex.test(formData.personal_info.email_address)) {
                    errors.push("Personal Email ID format is invalid");
                }
                if (directEmployeeOnboardFlow && formData.user_creation_info.email_address && !emailRegex.test(formData.user_creation_info.email_address)) {
                    errors.push("Professional Email format is invalid");
                }

                // Mobile number validation (basic - 10 digits)
                if (formData.personal_info.mobile_number && !/^\d{10}$/.test(formData.personal_info.mobile_number.replace(/\D/g, ''))) {
                    errors.push("Personal Mobile No should be 10 digits");
                }

                // Date validations
                if (formData.professional_info.date_of_joining && formData.personal_info.dob) {
                    const joiningDate = formData.professional_info.date_of_joining;
                    const dob = formData.personal_info.dob;
                    if (joiningDate.isBefore(dob)) {
                        errors.push("Date of Joining cannot be before Date of Birth");
                    }
                }

                if (formData.professional_info.date_of_joining && formData.professional_info.date_of_relieving) {
                    const joiningDate = formData.professional_info.date_of_joining;
                    const relievingDate = formData.professional_info.date_of_relieving;
                    if (relievingDate.isBefore(joiningDate)) {
                        errors.push("Date of Relieving cannot be before Date of Joining");
                    }
                }

                return errors;
            };

            // Run validation
            const validationErrors = validateForm();
            if (validationErrors.length > 0) {
                // Show first error, or you can show all errors
                ToastMsg('error', validationErrors[0]);
                // If you want to show all errors:
                // validationErrors.forEach(error => ToastMsg('error', error));
                return;
            }

            // Continue with your existing save logic...
            // Create a hybrid copy - shallow copy for files, deep copy for dates
            const modifiedInfo = { ...formData };

            // ... rest of your existing save code remains the same
            // Create separate deep copies for the JSON-serializable parts
            const professionalInfoCopy = JSON.parse(JSON.stringify(formData.professional_info));
            const personalInfoCopy = JSON.parse(JSON.stringify(formData.personal_info));
            const assetInfoCopy = formData.asset_info ? JSON.parse(JSON.stringify(formData.asset_info)) : [];

            // console.log("Modified Info before processing:", modifiedInfo);

            // Filter out empty asset info rows
            if (modifiedInfo.asset_info?.length > 0) {
                modifiedInfo.asset_info = modifiedInfo.asset_info.filter(row =>
                    row.asset_registry || row.asset_name || row.handing_over_by ||
                    (row.issued_date)
                );
            }

            // Format dates for professional info - Use original formData for dayjs objects
            if (formData.professional_info.date_of_joining) {
                professionalInfoCopy.date_of_joining = formData.professional_info.date_of_joining.format("YYYY-MM-DD");
            } else {
                professionalInfoCopy.date_of_joining = null;
            }

            if (formData.professional_info.date_of_relieving) {
                professionalInfoCopy.date_of_relieving = formData.professional_info.date_of_relieving.format("YYYY-MM-DD");
            } else {
                professionalInfoCopy.date_of_relieving = null;
            }

            // Format dates for personal info
            if (formData.personal_info.dob) {
                personalInfoCopy.dob = formData.personal_info.dob.format("YYYY-MM-DD");
            } else {
                personalInfoCopy.dob = null;
            }

            if (copyAddress) {
                personalInfoCopy.is_address_copy = copyAddress
            }

            // Format dates for asset info
            if (modifiedInfo.asset_info?.length > 0) {
                modifiedInfo.asset_info.forEach((item, index) => {
                    const originalItem = formData.asset_info[index];
                    if (originalItem?.issued_date) {
                        assetInfoCopy[index].issued_date = originalItem.issued_date.format("YYYY-MM-DD");
                    }
                });
            }

            // Handle pin codes - ensure they are numbers or null
            personalInfoCopy.pin_code = personalInfoCopy.pin_code ? parseInt(personalInfoCopy.pin_code) : null;
            personalInfoCopy.present_pin_code = personalInfoCopy.present_pin_code ? parseInt(personalInfoCopy.present_pin_code) : null;

            // Handle profile image upload - Use the shallow copy for file objects
            let profileFile = null;

            if (modifiedInfo.professional_info.profile_document &&
                modifiedInfo.professional_info.profile_document.length > 0) {

                let imgObj = { ...modifiedInfo.professional_info.profile_document[0] };
                // console.log("Image object:", imgObj);

                if (imgObj.hasOwnProperty('name')) {

                    professionalInfoCopy.profile = removeFileExtension(imgObj.name);

                    // Get the actual file for upload
                    profileFile = imgObj.originFileObj;

                    // Remove the profile_document array from professional_info copy
                    delete professionalInfoCopy.profile_document;
                }
            } else {
                // If no profile image, ensure profile field is not present or is null
                if (isEditMode && professionalInfoCopy.profile) {
                    // console.log("Keeping existing profile URL:", professionalInfoCopy.profile);
                    professionalInfoCopy.profile_document = professionalInfoCopy.profile;
                    delete professionalInfoCopy.profile
                } else {
                    professionalInfoCopy.profile = "";
                    professionalInfoCopy.profile_document = [];
                }
            }

            // Prepare bank_info with empty strings for missing fields
            const bankInfo = {
                id: isEditMode ? modifiedInfo.bank_info.id || "" : "",
                bank_name: modifiedInfo.bank_info.bank_name || "",
                account_number: modifiedInfo.bank_info.account_number || "",
                ifsc_code: modifiedInfo.bank_info.ifsc_code || "",
                account_holder_name: modifiedInfo.bank_info.account_holder_name || "",
                branch: modifiedInfo.bank_info.branch || ""
            };

            // Prepare user_creation_info with id field
            const userCreationInfo = {
                ...modifiedInfo.user_creation_info,
                id: isEditMode ? modifiedInfo.user_creation_info.user_id || "" : ""
            };

            // Prepare the payload in the exact format your old API expects
            const formEmpData = new FormData();

            // Append each section as JSON string using the processed copies
            formEmpData.append("professional_info", JSON.stringify(professionalInfoCopy));
            formEmpData.append("personal_info", JSON.stringify(personalInfoCopy));
            formEmpData.append("bank_info", JSON.stringify(bankInfo));
            formEmpData.append("asset_info", assetInfoCopy.length > 0 ? JSON.stringify(assetInfoCopy) : JSON.stringify([]));
            formEmpData.append("user_creation_info", JSON.stringify(userCreationInfo));

            if (profileFile && profileFile instanceof File) {
                formEmpData.append("professional_info__profile", profileFile);
            } else {
                // If no profile, send empty array like your old code
                formEmpData.append("professional_info__profile", '');
            }

            // Append employee_id if in edit mode
            if (isEditMode) {
                formEmpData.append("employee_id", employeeID);
            }

            let apiMethod = isEditMode ? update : create;

            // Use your custom create method with FormData
            const response = await apiMethod(
                url,
                formEmpData,
                true,
                setLoading
            );

            if (response.status !== 'fail') {
                ToastMsg('success', response.message);

                // Reset form data
                setFormData({
                    professional_info: {
                        is_active: true,
                        profile_document: [],
                        employee_id: null,
                        display_name: null,
                        department: undefined,
                        designation: undefined,
                        reporting_manager: undefined,
                        shift: undefined,
                        employee_group: undefined,
                        employee_type: undefined,
                        work_location: null,
                        date_of_joining: null,
                        date_of_relieving: null,
                    },
                    personal_info: {
                        title: undefined,
                        first_name: null,
                        last_name: null,
                        gender: undefined,
                        dob: null,
                        mobile_number: null,
                        email_address: null,
                        permanent_address: null,
                        pin_code: null,
                        city: null,
                        state: null,
                        country: null,
                        contact_person: null,
                        relationship: undefined,
                        contact_person_mobile_number: null,
                        is_admin: false,
                        blood_group: undefined,
                        present_address: null,
                        present_pin_code: null,
                        present_city: null,
                        present_state: null,
                        present_country: null,
                        emergency_contact_person: null,
                        emergency_contact_person_relationship: null,
                        emergency_contact_person_mobile_number: null,
                    },
                    bank_info: {
                        bank_name: null,
                        account_number: null,
                        ifsc_code: null,
                        account_holder_name: null,
                        branch: null,
                    },
                    asset_info: default_asset_info.map(obj => ({ ...obj })),
                    user_creation_info: {
                        user_name: null,
                        password: null,
                        user_group: undefined,
                        email_address: null
                    }
                });

                if (isEditMode) {
                    handleBack();
                }
            } else {
                ToastMsg('error', response.message);
            }

        } catch (error) {
            console.error('Save error:', error);
            ToastMsg('error', error.message || 'An error occurred while saving');
        } finally {
            setLoading(false);
        }
    };

    if (screenLoader) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex items-center justify-center rounded-md">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
            </div>
        );
    }

    return (
        <ContainerCard>
            {/* Header Section */}
            <Header
                onBack={handleBack}
                title={isEditMode ? "Edit Employee" : pagetitle}
                description={isEditMode
                    ? "Update employee personal and professional details"
                    : "Onboard employee with their personal and professional details"
                }
                role={formData.personal_info.is_admin ? "Admin" : "Employee"}
                isAdmin={formData.personal_info.is_admin}
                onRoleToggle={handleRoleToggle}
            />

            {/* Main Form Grid */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-6 h-[calc(100vh-250px)] overflow-y-auto">
                <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">

                    {/* Left Column - Profile & Professional Info */}
                    <div className="xl:col-span-5 space-y-8">
                        {/* Profile Upload */}
                        <div className="flex justify-center xl:justify-start">
                            <ProfileUpload onFileUpload={handleFileUpload} profile={profile} />
                        </div>

                        {/* Professional Information */}
                        <FormSection title="Professional Information">
                            <div className="grid grid-cols-1 gap-6">
                                <FormField label="Display Name" required>
                                    <Input
                                        value={formData.professional_info.display_name}
                                        onChange={(e) => handleInputChange('professional_info', 'display_name', e.target.value)}
                                        placeholder="Enter display name"
                                        className={formInput}
                                        size="large"
                                    />
                                </FormField>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    <FormField label="Department" required>
                                        <Select
                                            value={formData.professional_info.department}
                                            onChange={(value) => handleInputChange('professional_info', 'department', value)}
                                            placeholder="Select department"
                                            className={formSelect}
                                            size="large"
                                        >
                                            {dropdownData.department.map(dept => (
                                                <Option key={dept.id} value={dept.id}>
                                                    {dept.department_name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </FormField>

                                    <FormField label="Designation" required>
                                        <Select
                                            value={formData.professional_info.designation}
                                            onChange={(value) => handleInputChange('professional_info', 'designation', value)}
                                            placeholder="Select designation"
                                            className={formSelect}
                                            size="large"
                                        >
                                            {dropdownData.designation.map(desig => (
                                                <Option key={desig.id} value={desig.id}>
                                                    {desig.designation_name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </FormField>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    <FormField label="Reporting Manager">
                                        <Select
                                            value={formData.professional_info.reporting_manager}
                                            onChange={(value) => handleInputChange('professional_info', 'reporting_manager', value)}
                                            placeholder="Select reporting manager"
                                            className={formSelect}
                                            size="large"
                                        >
                                            {dropdownData.empinfo.map(emp => (
                                                <Option key={emp.id} value={emp.id}>
                                                    {`${emp.first_name} ${emp.last_name}`}
                                                </Option>
                                            ))}
                                        </Select>
                                    </FormField>

                                    <FormField label="Employee Group">
                                        <Select
                                            value={formData.professional_info.employee_group}
                                            onChange={(value) => handleInputChange('professional_info', 'employee_group', value)}
                                            placeholder="Select Employee Group"
                                            className={formSelect}
                                            size="large"
                                        >
                                            {dropdownData.empgroup.map(group => (
                                                <Option key={group.id} value={group.id}>
                                                    {group.employee_group_name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </FormField>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    <FormField label="Shift" required>
                                        <Select
                                            value={formData.professional_info.shift}
                                            onChange={(value) => handleInputChange('professional_info', 'shift', value)}
                                            placeholder="Select shift"
                                            className={formSelect}
                                            size="large"
                                        >
                                            {dropdownData.shift.map(shift => (
                                                <Option key={shift.id} value={shift.id}>
                                                    {shift.shift_name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </FormField>

                                    <FormField label="Employee Type" required>
                                        <Select
                                            value={formData.professional_info.employee_type}
                                            onChange={(value) => handleInputChange('professional_info', 'employee_type', value)}
                                            placeholder="Select employee type"
                                            className={formSelect}
                                            size="large"
                                        >
                                            {dropdownData.emptype.map(type => (
                                                <Option key={type.id} value={type.id}>
                                                    {type.employee_type_name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </FormField>
                                </div>

                                <FormField label="Work Location">
                                    <Input
                                        value={formData.professional_info.work_location}
                                        onChange={(e) => handleInputChange('professional_info', 'work_location', e.target.value)}
                                        placeholder="Enter work location"
                                        className={formInput}
                                    />
                                </FormField>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    <FormField label="Date of Joining" required>
                                        <DatePicker
                                            value={formData.professional_info.date_of_joining}
                                            onChange={(date) => handleInputChange('professional_info', 'date_of_joining', date)}
                                            className={formDatepicker}
                                            size="large"
                                            placeholder="Select date"
                                        />
                                    </FormField>

                                    <FormField label="Date of Relieving">
                                        <DatePicker
                                            value={formData.professional_info.date_of_relieving}
                                            onChange={(date) => handleInputChange('professional_info', 'date_of_relieving', date)}
                                            className={formDatepicker}
                                            size="large"
                                            placeholder="Select date"
                                        />
                                    </FormField>
                                </div>
                            </div>
                        </FormSection>
                        {directEmployeeOnboardFlow && (
                            <>
                                {/* User Creation */}
                                <FormSection title="User Credentials">
                                    <div className="grid grid-cols-2 gap-6">
                                        <FormField label="Username" required>
                                            <Input
                                                value={formData.user_creation_info.user_name}
                                                onChange={(e) => handleInputChange('user_creation_info', 'user_name', e.target.value)}
                                                placeholder="Enter Username"
                                                className={formInput}
                                                size="large"
                                            />
                                        </FormField>

                                        <FormField label="Password" required>
                                            <PasswordInput
                                                value={formData.user_creation_info.password}
                                                onChange={(e) => handleInputChange('user_creation_info', 'password', e.target.value)}
                                                placeholder="Enter Password"
                                                className={formInput}
                                                size="large"
                                            />
                                        </FormField>

                                        <FormField label="User Group" required>
                                            <Select
                                                value={formData.user_creation_info.user_group}
                                                onChange={(value) => handleInputChange('user_creation_info', 'user_group', value)}
                                                placeholder="Select User Group"
                                                className={formSelect}
                                                size="large"
                                            >
                                                {dropdownData.usergroup.map(group => (
                                                    <Option key={group.id} value={group.id}>
                                                        {group.user_group_name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </FormField>

                                        <FormField label="Professional Email" required>
                                            <Input
                                                value={formData.user_creation_info.email_address}
                                                onChange={(e) => handleInputChange('user_creation_info', 'email_address', e.target.value)}
                                                placeholder="Enter Professional Email"
                                                className={formInput}
                                                size="large"
                                            />
                                        </FormField>
                                    </div>
                                </FormSection>
                                {/* Account Information */}
                                <FormSection title="Account Information">
                                    <div className="grid grid-cols-2 gap-6 mb-3">
                                        <FormField label="Bank Name">
                                            <Input
                                                value={formData.bank_info.bank_name}
                                                onChange={(e) => handleInputChange('bank_info', 'bank_name', e.target.value)}
                                                placeholder="Enter Bank Name"
                                                className={formInput}
                                                size="large"
                                            />
                                        </FormField>
                                        <FormField label="Account Number">
                                            <Input
                                                value={formData.bank_info.account_number}
                                                onChange={(e) => handleInputChange('bank_info', 'account_number', e.target.value)}
                                                placeholder="Enter Account Number"
                                                className={formInput}
                                                size="large"
                                            />
                                        </FormField>
                                        <FormField label="IFSC Code">
                                            <Input
                                                value={formData.bank_info.ifsc_code}
                                                onChange={(e) => handleInputChange('bank_info', 'ifsc_code', e.target.value)}
                                                placeholder="Enter IFSC Code"
                                                className={formInput}
                                                size="large"
                                            />
                                        </FormField>
                                        <FormField label="Account Holder Name">
                                            <Input
                                                value={formData.bank_info.account_holder_name}
                                                onChange={(e) => handleInputChange('bank_info', 'account_holder_name', e.target.value)}
                                                placeholder="Enter Account Holder Name"
                                                className={formInput}
                                                size="large"
                                            />
                                        </FormField>
                                    </div>
                                    <FormField label="Branch">
                                        <Input
                                            value={formData.bank_info.branch}
                                            onChange={(e) => handleInputChange('bank_info', 'branch', e.target.value)}
                                            placeholder="Enter Branch"
                                            className={formInput}
                                            size="large"
                                        />
                                    </FormField>
                                </FormSection>
                            </>
                        )}
                    </div>

                    {/* Right Column - Personal Information */}
                    <div className="xl:col-span-7">
                        <FormSection title="Personal Information">
                            <div className="grid grid-cols-1 gap-6">
                                {/* Name Section */}
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <div className="md:col-span-1">
                                        <FormField label="Title" required>
                                            <Select
                                                value={formData.personal_info.title}
                                                onChange={(value) => handleInputChange('personal_info', 'title', value)}
                                                placeholder="Title"
                                                className={formSelect}
                                                size="large"
                                            >
                                                {titleEnum.map(title => (
                                                    <Option key={title.value} value={title.value}>
                                                        {title.text}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </FormField>
                                    </div>
                                    <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <FormField label="First Name" required>
                                            <Input
                                                value={formData.personal_info.first_name}
                                                onChange={(e) => handleInputChange('personal_info', 'first_name', e.target.value)}
                                                placeholder="First name"
                                                className={formInput}
                                            />
                                        </FormField>
                                        <FormField label="Last Name" required>
                                            <Input
                                                value={formData.personal_info.last_name}
                                                onChange={(e) => handleInputChange('personal_info', 'last_name', e.target.value)}
                                                placeholder="Last name"
                                                className={formInput}
                                            />
                                        </FormField>
                                    </div>
                                </div>

                                {/* Personal Details */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <FormField label="Gender" required>
                                        <Select
                                            value={formData.personal_info.gender}
                                            onChange={(value) => handleInputChange('personal_info', 'gender', value)}
                                            placeholder="Select gender"
                                            className={formSelect}
                                            size="large"
                                        >
                                            {genderEnum.map(gender => (
                                                <Option key={gender.value} value={gender.value}>
                                                    {gender.text}
                                                </Option>
                                            ))}
                                        </Select>
                                    </FormField>

                                    <FormField label="Blood Group">
                                        <Select
                                            value={formData.personal_info.blood_group}
                                            onChange={(value) => handleInputChange('personal_info', 'blood_group', value)}
                                            placeholder="Blood group"
                                            className={formSelect}
                                            size="large"
                                        >
                                            {bloodGroupEnum.map(blood => (
                                                <Option key={blood.value} value={blood.value}>
                                                    {blood.text}
                                                </Option>
                                            ))}
                                        </Select>
                                    </FormField>

                                    <FormField label="Date of Birth" required>
                                        <DatePicker
                                            value={formData.personal_info.dob}
                                            onChange={(date) => handleInputChange('personal_info', 'dob', date)}
                                            className={formDatepicker}
                                            size="large"
                                            placeholder="Date of birth"
                                        />
                                    </FormField>
                                </div>

                                {/* Contact Information */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <FormField label="Personal Mobile No" required>
                                        <Input
                                            value={formData.personal_info.mobile_number}
                                            onChange={(e) => handleInputChange('personal_info', 'mobile_number', e.target.value)}
                                            placeholder="Mobile number"
                                            className={formInput}
                                        />
                                    </FormField>

                                    <FormField label="Personal Email ID" required>
                                        <Input
                                            value={formData.personal_info.email_address}
                                            onChange={(e) => handleInputChange('personal_info', 'email_address', e.target.value)}
                                            placeholder="Email address"
                                            type="email"
                                            className={formInput}
                                        />
                                    </FormField>
                                </div>

                                {/* Address Sections */}
                                <div className="space-y-6">
                                    {/* Permanent Address */}
                                    <AddressSection
                                        title="Permanent Address"
                                        prefix="permanent"
                                        formData={formData.personal_info}
                                        onInputChange={(field, value) => handleInputChange('personal_info', field, value)}
                                    />

                                    {/* Contact Person */}
                                    <div className="grid grid-cols-1 gap-4 p-4 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg">
                                        <FormField label="Contact Person Name">
                                            <Input
                                                value={formData.personal_info.contact_person}
                                                onChange={(e) => handleInputChange('personal_info', 'contact_person', e.target.value)}
                                                placeholder="Contact person name"
                                                className={formInput}
                                            />
                                        </FormField>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <FormField label="Relationship Type">
                                                <Select
                                                    value={formData.personal_info.relationship}
                                                    onChange={(value) => handleInputChange('personal_info', 'relationship', value)}
                                                    placeholder="Relationship"
                                                    className={formSelect}
                                                    size="large"
                                                >
                                                    {relationshipEnum.map(rel => (
                                                        <Option key={rel.value} value={rel.value}>
                                                            {rel.text}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </FormField>
                                            <FormField label="Contact Person Mobile">
                                                <Input
                                                    value={formData.personal_info.contact_person_mobile_number}
                                                    onChange={(e) => handleInputChange('personal_info', 'contact_person_mobile_number', e.target.value)}
                                                    placeholder="Mobile number"
                                                    className={formInput}
                                                />
                                            </FormField>
                                        </div>
                                    </div>

                                    {/* Copy Address Toggle */}
                                    <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                        <Checkbox
                                            type="checkbox"
                                            id="copyAddress"
                                            checked={copyAddress}
                                            onChange={(e) => handleCopyAddressToggle(e.target.checked)}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                                        />
                                        <label htmlFor="copyAddress" className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 cursor-pointer">
                                            <Copy className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                            Copy Permanent Address to Present Address
                                        </label>
                                    </div>

                                    {/* Present Address */}
                                    <AddressSection
                                        title="Present Address"
                                        prefix="present"
                                        formData={formData.personal_info}
                                        onInputChange={(field, value) => handleInputChange('personal_info', field, value)}
                                    />

                                    {/* Emergency Contact */}
                                    <div className="grid grid-cols-1 gap-4 p-4 bg-red-50/50 dark:bg-red-900/10 rounded-lg border border-red-100 dark:border-red-800">
                                        <FormField label="Emergency Contact Person">
                                            <Input
                                                value={formData.personal_info.emergency_contact_person}
                                                onChange={(e) => handleInputChange('personal_info', 'emergency_contact_person', e.target.value)}
                                                placeholder="Emergency contact"
                                                className={formInput}
                                            />
                                        </FormField>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <FormField label="Relationship Type">
                                                <Select
                                                    value={formData.personal_info.emergency_contact_person_relationship}
                                                    onChange={(value) => handleInputChange('personal_info', 'emergency_contact_person_relationship', value)}
                                                    placeholder="Relationship"
                                                    className={formSelect}
                                                    size="large"
                                                >
                                                    {relationshipEnum.map(rel => (
                                                        <Option key={rel.value} value={rel.value}>
                                                            {rel.text}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </FormField>

                                            <FormField label="Emergency Contact No">
                                                <Input
                                                    value={formData.personal_info.emergency_contact_person_mobile_number}
                                                    onChange={(e) => handleInputChange('personal_info', 'emergency_contact_person_mobile_number', e.target.value)}
                                                    placeholder="Emergency number"
                                                    className={formInput}
                                                />
                                            </FormField>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </FormSection>
                    </div>
                </div>

                {/* Asset information */}
                {directEmployeeOnboardFlow && (
                    <div className="mt-8">
                        <FormSection title='Asset Information'>
                            {formData.asset_info.map((asset, index) => (
                                <div key={index} className="mb-6 border-gray-200 dark:border-gray-700 rounded-lg">
                                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                                        <FormField label="Asset Registry">
                                            <Select
                                                value={asset.asset_registry}
                                                onChange={(value) => handleAssetRegistryChange(index, value)}
                                                placeholder="Select Asset Registry"
                                                className={formSelect}
                                                size="large"
                                            >
                                                {dropdownData.assetreg.map(assetItem => (
                                                    <Option key={assetItem.id} value={assetItem.id}>
                                                        {assetItem.asset_id} - {assetItem.asset_name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </FormField>

                                        <FormField label="Asset Name">
                                            <Input
                                                value={asset.asset_name}
                                                onChange={(e) => handleAssetChange(index, 'asset_name', e.target.value)}
                                                placeholder="Asset Name"
                                                className={formInput}
                                                disabled
                                            />
                                        </FormField>

                                        <FormField label="Hand over by">
                                            <Select
                                                value={asset.handing_over_by}
                                                onChange={(value) => handleAssetChange(index, 'handing_over_by', value)}
                                                placeholder="Select Handover By"
                                                className={formSelect}
                                                size="large"
                                            >
                                                {dropdownData.empinfo.map(emp => (
                                                    <Option key={emp.id} value={emp.id}>
                                                        {`${emp.first_name} ${emp.last_name}`}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </FormField>

                                        <FormField label="Hand over date">
                                            <DatePicker
                                                value={asset.issued_date}
                                                onChange={(date) => handleAssetChange(index, 'issued_date', date)}
                                                className={formDatepicker}
                                                size="large"
                                                placeholder="Select date"
                                            />
                                        </FormField>
                                    </div>

                                    <div className="mt-4">
                                        <FormField label="Remarks">
                                            <Textarea
                                                value={asset.remarks}
                                                onChange={(e) => handleAssetChange(index, 'remarks', e.target.value)}
                                                placeholder="Enter Remarks"
                                                rows={3}
                                                showCharCount={true}
                                                maxLength={250}
                                                className={formTextarea}
                                            />
                                        </FormField>

                                        <div className="flex justify-end mt-3 gap-3">
                                            {index === formData.asset_info.length - 1 && (
                                                <Button
                                                    variant='outline'
                                                    onClick={handleAddAsset}
                                                    disabled={!asset.asset_registry && !asset.asset_name}
                                                    className="bg-sidebar text-white px-3 py-2 hover:bg-sidebar-primary"
                                                >
                                                    <Plus /> Add More
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </FormSection>
                    </div>
                )}
            </div>

            <div className="flex justify-end items-center gap-3 mt-3 p-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                {/* Cancel Button */}
                <Button
                    variant="outline"
                    onClick={handleBack}
                    disabled={loading}
                    className="flex items-center gap-2"
                >
                    <X className="w-4 h-4" />
                    Cancel
                </Button>

                {/* Create / Update Button */}
                <Button
                    disabled={loading}
                    onClick={handleSave}
                    className={cn("bg-sidebar dark:bg-sidebar-primary hover:dark:bg-sidebar-primary/80 hover:bg-sidebar-primary", "text-white transition-colors duration-200", "flex items-center gap-2")}
                >
                    {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                        <Save className="w-4 h-4" />
                    )}
                    {loading ? "Processing..." : (isEditMode ? "Update Employee" : "Create Employee")}
                </Button>
            </div>
        </ContainerCard>
    );
});

// Sub-components (keep the same as before)
const Header = ({ onBack, title, description, role, isAdmin, onRoleToggle }) => (
    <div className="flex items-center gap-4 mb-6">
        <ChevronLeftIcon
            className="h-5 w-5 cursor-pointer"
            onClick={onBack}
        />

        <div className="flex-1">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                {title}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
                {description}
            </p>
        </div>

        <RoleToggle
            role={role}
            isAdmin={isAdmin}
            onToggle={onRoleToggle}
        />
    </div>
);

const RoleToggle = ({ role, isAdmin, onToggle }) => (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
        <div className="flex items-center gap-3">
            <span className={cn(
                "text-sm font-medium",
                isAdmin
                    ? "text-green-600 dark:text-green-400"
                    : "text-gray-500 dark:text-gray-400"
            )}>
                {role}
            </span>
            <button
                type="button"
                onClick={onToggle}
                className={cn(
                    "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
                    isAdmin ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
                    "cursor-pointer"
                )}
            >
                <span
                    className={cn(
                        "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                        isAdmin ? "translate-x-4" : "translate-x-1"
                    )}
                />
            </button>
        </div>
    </div>
);

const ProfileUpload = ({ onFileUpload, profile = null }) => {
    const [previewImage, setPreviewImage] = useState(profile);
    const [imageError, setImageError] = useState(false);

    // Update preview when profile prop changes
    useEffect(() => {
        setPreviewImage(profile);
        setImageError(false); // Reset error when profile changes
    }, [profile]);

    const validateFile = (file) => {
        const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB in bytes
        const isValidSize = file.size <= MAX_FILE_SIZE;

        if (!isValidSize) {
            ToastMsg('error', 'File size must be less than 1MB');
            return false;
        }

        return true;
    };

    const handleImgChange = (file) => {
        if (file?.length > 0 && file[0]?.originFileObj) {
            const uploadedFile = file[0].originFileObj;

            // Validate file size
            if (!validateFile(uploadedFile)) {
                return; // Don't proceed if validation fails
            }

            const imageUrl = URL.createObjectURL(uploadedFile);
            setPreviewImage(imageUrl);
            setImageError(false);
            if (onFileUpload) {
                onFileUpload(file);
            }
        } else if (file?.length === 0) {
            setPreviewImage(null);
            setImageError(false);
            if (onFileUpload) {
                onFileUpload(null);
            }
        }
    };

    const handleRemoveImage = (e) => {
        e.stopPropagation();
        setPreviewImage(null);
        setImageError(false);
        if (onFileUpload) {
            onFileUpload(null, 'removed');
        }
    };

    const handleImageError = () => {
        setImageError(true);
    };

    return (
        <div className="flex flex-col items-center gap-4 p-6 bg-gray-50 dark:bg-gray-700/50 rounded-2xl border border-gray-200 dark:border-gray-600">
            <div className="relative">
                <Upload
                    accept="image/*"
                    maxCount={1}
                    beforeUpload={() => false}
                    onChange={({ fileList }) => handleImgChange(fileList)}
                    showUploadList={false}
                    className="block"
                >
                    <div className="w-28 h-28 rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-800 flex items-center justify-center shadow-sm cursor-pointer group relative">
                        {previewImage && !imageError ? (
                            <>
                                <img
                                    src={previewImage}
                                    alt="Profile preview"
                                    className="w-full h-full object-cover"
                                    onError={handleImageError}
                                />
                                <button
                                    onClick={handleRemoveImage}
                                    className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                                >
                                    <svg
                                        className="w-3 h-3 text-white"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </>
                        ) : (
                            <UserIcon className="w-10 h-10 text-gray-400" />
                        )}
                    </div>
                </Upload>

                {(!previewImage || imageError) && (
                    <div className="absolute -bottom-2 -right-2 w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center border-2 border-white dark:border-gray-800 shadow-lg">
                        <UploadCloudIcon className="w-4 h-4 text-white" />
                    </div>
                )}
            </div>
            <div className="text-center">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Profile Photo
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                    JPG, PNG • Max 1MB
                </p>
                {previewImage && !imageError && (
                    <button
                        onClick={handleRemoveImage}
                        className="mt-2 text-xs text-red-500 hover:text-red-600 transition-colors duration-200 underline"
                    >
                        Remove Photo
                    </button>
                )}
            </div>
        </div>
    );
};

const AddressSection = ({ title, prefix, formData, onInputChange }) => (
    <div className="space-y-4 p-4 bg-gray-50/50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{title}</h4>
        <FormField label="Address">
            <Textarea
                value={formData[`${prefix}_address`]}
                onChange={(e) => onInputChange(`${prefix}_address`, e.target.value)}
                placeholder={`Enter ${title.toLowerCase()}`}
                rows={7}
                showCharCount={true}
                maxLength={250}
                className={formTextarea}
            />
        </FormField>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <FormField label="Pincode">
                <Input
                    value={formData[`${prefix === 'permanent' ? 'pin_code' : 'present_pin_code'}`]}
                    onChange={(e) => onInputChange(`${prefix === 'permanent' ? 'pin_code' : 'present_pin_code'}`, e.target.value)}
                    placeholder="Pincode"
                    className={formInput}
                />
            </FormField>
            <FormField label="City" required>
                <Input
                    value={formData[`${prefix === 'permanent' ? 'city' : 'present_city'}`]}
                    onChange={(e) => onInputChange(`${prefix === 'permanent' ? 'city' : 'present_city'}`, e.target.value)}
                    placeholder="City"
                    className={formInput}
                />
            </FormField>
            <FormField label="State" required>
                <Input
                    value={formData[`${prefix === 'permanent' ? 'state' : 'present_state'}`]}
                    onChange={(e) => onInputChange(`${prefix === 'permanent' ? 'state' : 'present_state'}`, e.target.value)}
                    placeholder="State"
                    className={formInput}
                />
            </FormField>
            <FormField label="Country">
                <Input
                    value={formData[`${prefix === 'permanent' ? 'country' : 'present_country'}`]}
                    onChange={(e) => onInputChange(`${prefix === 'permanent' ? 'country' : 'present_country'}`, e.target.value)}
                    placeholder="Country"
                    className={formInput}
                />
            </FormField>
        </div>
    </div>
);

const PasswordInput = ({ value, onChange }) => {
    const [showPassword, setShowPassword] = useState(false);

    return (
        <div className="relative">
            <Input
                type={showPassword ? "text" : "password"}
                name="password"
                value={value}
                onChange={onChange}
                placeholder="Enter your password"
                className={formInput}
            />
            <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-2 top-3 text-gray-500 hover:text-sidebar text-xs"
            >
                {showPassword ? <Eye className="w-4 h-4" /> : <EyeClosed className="w-4 h-4" />}
            </button>
        </div>
    );
};

const FormSection = ({ title, children }) => (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 pb-3 border-b border-gray-200 dark:border-gray-700">
            {title}
        </h3>
        {children}
    </div>
);

const FormField = ({ label, required = false, children, className }) => (
    <div className={cn("space-y-2", className)}>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
            {label}
            {required && <span className="text-red-500 text-xs">*</span>}
        </label>
        {children}
    </div>
);

// Add these CSS classes to your global CSS or create a utility
const formStyles = {
    input: cn(
        "w-full rounded-md transition-colors duration-200",
        "border-gray-300 dark:border-gray-600",
        "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
        "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
    ),
    select: cn(
        "w-full rounded-md transition-colors duration-200",
        "border-gray-300 dark:border-gray-600",
        "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
        "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
    ),
    textarea: cn(
        "w-full rounded-md transition-colors duration-200",
        "border-gray-300 dark:border-gray-600",
        "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
        "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400 resize-none"
    ),
    datepicker: cn(
        "w-full rounded-md transition-colors duration-200",
        "border-gray-300 dark:border-gray-600",
        "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
        "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
    )
};

// Apply styles to components
const { input: formInput, select: formSelect, textarea: formTextarea, datepicker: formDatepicker } = formStyles;

export default EmployeeForm;